import { QLookup, QModalHeader, QText, useToastProvider } from '@qualio/ui-components';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addUsersToGroup, removeUsersfromGroup } from '../../api/medtech-api';
import { useGroupLookupView } from '../../hooks/useGroupLookupView';
import { Group, ModalComponentProps, User } from '../../types';
import { removeUsersFromGroupsUnlessContentHidden } from '../../utils/groups.utils';
import { createErrorToast, createSuccessToast, createWarningToast } from '../../utils/toast.utils';
import { ContentHiddenConfirmationModal } from './ContentHiddenConfirmationModal';

type ManageGroupsModalProps = ModalComponentProps & {
  users: User[];
  groups: Group[];
  getUsers: () => Promise<void>;
};

export const ManageGroupsModal: React.FC<ManageGroupsModalProps> = ({ closeModal, users, groups, getUsers }) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();

  const [isSaving, setSaving] = useState(false);
  const [isForceConfirmOpen, setForceConfirmOpen] = useState(false);
  const [groupsNeedingForce, setGroupsNeedingForce] = useState<number[]>([]);
  const [pendingUserIds, setPendingUserIds] = useState<number[]>([]);

  const preSelectedGroupIds = useMemo(() => new Set(users.flatMap((u) => u.groups.map((g) => g.id))), [users]);

  const isGroupPreSelected = useCallback((group: Group) => preSelectedGroupIds.has(group.id), [preSelectedGroupIds]);

  const { groupDataView, filterOption } = useGroupLookupView();

  const onSelect = useCallback(
    async (selectedGroups: readonly Group[]) => {
      if (isSaving) return;
      setSaving(true);

      const allUserIds = users.map((u) => u.id);
      const selectedGroupIds = new Set(selectedGroups.map((g) => g.id));

      const groupsToAdd = selectedGroups.filter((g) => !users.every((u) => u.groups.some((ug) => ug.id === g.id)));
      const groupIdsToRemove = Array.from(preSelectedGroupIds).filter((id) => !selectedGroupIds.has(id));

      try {
        if (groupsToAdd.length > 0) {
          await addUsersToGroup({ groups: groupsToAdd.map((g) => g.id), users: allUserIds });
        }

        const { groupIdsNeedingForce, warningKeys } = await removeUsersFromGroupsUnlessContentHidden(
          groupIdsToRemove,
          allUserIds,
        );

        if (groupIdsNeedingForce.length > 0) {
          warningKeys.forEach((warningKey) =>
            showToast(createWarningToast(t(warningKey), 'manage-groups-hidden-docs-warning-toast')),
          );
          setGroupsNeedingForce(groupIdsNeedingForce);
          setPendingUserIds(allUserIds);
          setForceConfirmOpen(true);
        } else {
          await getUsers();
          showToast(createSuccessToast(t('messages.success.groupsUpdated'), 'manage-groups-success-toast'));
          closeModal();
        }
      } catch {
        showToast(createErrorToast(t('messages.error.manageGroups'), 'manage-groups-error-toast'));
        closeModal();
      } finally {
        setSaving(false);
      }
    },
    [isSaving, users, preSelectedGroupIds, getUsers, closeModal, showToast, t],
  );

  const handleForceConfirm = useCallback(async () => {
    setForceConfirmOpen(false);
    setSaving(true);
    try {
      await Promise.all(
        groupsNeedingForce.map((groupId) =>
          removeUsersfromGroup({ groups: [groupId], users: pendingUserIds, forceRemove: true }),
        ),
      );
      await getUsers();
      showToast(createSuccessToast(t('messages.success.groupsUpdated'), 'manage-groups-success-toast'));
      closeModal();
    } catch {
      showToast(createErrorToast(t('messages.error.manageGroups'), 'manage-groups-error-toast'));
      closeModal();
    } finally {
      setSaving(false);
      setGroupsNeedingForce([]);
    }
  }, [groupsNeedingForce, pendingUserIds, getUsers, closeModal, showToast, t]);

  return (
    <>
      <QLookup.DataProvider.Fixed data={groups} filterOption={filterOption}>
        <QLookup.MultiSelect<Group>
          isOpen
          action={isSaving ? t('modals.manageGroups.saving') : t('modals.manageGroups.save')}
          searchPlaceholder={t('modals.manageGroups.searchPlaceholder')}
          accessors={{ id: 'id' }}
          isItemDisabled={() => false}
          isItemPreSelected={isGroupPreSelected}
          onSelect={onSelect}
          onCancel={closeModal}
          view={groupDataView}
          defaultSortBy={{ id: 'name' }}
        >
          <QModalHeader>
            <QText>{t('modals.manageGroups.title')}</QText>
          </QModalHeader>
        </QLookup.MultiSelect>
      </QLookup.DataProvider.Fixed>

      {isForceConfirmOpen && (
        <ContentHiddenConfirmationModal
          closeModal={() => setForceConfirmOpen(false)}
          onConfirm={handleForceConfirm}
          isLoading={isSaving}
        />
      )}
    </>
  );
};
